export const deployModule = `# 通用模組：Cloudflare Workers + Pages 部署基礎設施

> 版本：v1.0
> 適用場景：任何需要部署到 Cloudflare 的前後端分離專案
> 輸入：專案名稱、子網域、KV namespace ID（可選）、R2 bucket 名稱（可選）
> 輸出：wrangler.toml、GitHub Actions workflow、deploy 腳本、環境變數模板

---

## 1. 技術約束

- **Worker 框架**：Hono（TypeScript）
- **前端框架**：Vite + React + TypeScript
- **部署工具**：Wrangler 3 + GitHub Actions
- **Secrets 管理**：wrangler secret（生產）/ .dev.vars（本地）

---

## 2. 目錄結構約定

\`\`\`
<project-root>/
├── apps/
│   ├── web/                  # Vite React 前端
│   │   ├── .env              # VITE_CMS_API_URL（本地開發）
│   │   ├── .env.example      # 佔位符模板
│   │   └── ...
│   └── api/                  # Cloudflare Worker（Hono）
│       ├── wrangler.toml
│       └── ...
├── scripts/
│   └── deploy.ps1            # 本地部署腳本
├── .github/
│   └── workflows/
│       └── deploy.yml        # CI/CD
└── package.json              # root scripts
\`\`\`

---

## 3. wrangler.toml 模板

\`\`\`toml
name = "{{PROJECT_NAME}}"
main = "src/index.ts"
compatibility_date = "2025-07-18"

[[kv_namespaces]]
binding = "{{KV_BINDING_NAME}}"
id = "{{KV_NAMESPACE_ID}}"

# 可選 R2
# [[r2_buckets]]
# binding = "{{R2_BINDING_NAME}}"
# bucket_name = "{{R2_BUCKET_NAME}}"

[vars]
ADMIN_USERNAME = "{{ADMIN_USERNAME}}"
\`\`\`

**規則**：
- \`ADMIN_PASSWORD\` **絕對不可**寫入 \`[vars]\`，必須用 \`wrangler secret put ADMIN_PASSWORD\`
- 本地開發使用 \`.dev.vars\` 存放密碼
- \`.dev.vars\` 與 \`.env\` 必須列入 \`.gitignore\`

---

## 4. 根 package.json scripts

\`\`\`json
{
  "scripts": {
    "dev": "concurrently \\"cd apps/web && npm run dev\\" \\"cd apps/api && npm run dev\\"",
    "build": "cd apps/web && npm run build",
    "deploy": "cd apps/api && npm run deploy && cd ../web && npm run deploy",
    "deploy:worker": "cd apps/api && npm run deploy",
    "deploy:web": "cd apps/web && npm run build && npm run deploy"
  }
}
\`\`\`

---

## 5. GitHub Actions 模板

\`\`\`yaml
name: Deploy to Cloudflare

on:
  push:
    branches: [main]

jobs:
  deploy-worker:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: '20'
          cache: 'npm'
      - run: npm ci
      - name: Deploy Worker
        uses: cloudflare/wrangler-action@v3
        with:
          apiToken: \${{ secrets.CLOUDFLARE_API_TOKEN }}
          accountId: \${{ secrets.CLOUDFLARE_ACCOUNT_ID }}
          workingDirectory: apps/api
          command: deploy

  deploy-web:
    needs: deploy-worker
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: actions/setup-node@v4
        with:
          node-version: '20'
          cache: 'npm'
      - run: npm ci
      - name: Fetch CMS data
        run: node apps/web/scripts/fetch-cms.cjs
        env:
          CMS_API_URL: \${{ secrets.CMS_API_URL }}
      - name: Build
        run: cd apps/web && npm run build
        env:
          VITE_CMS_API_URL: \${{ secrets.CMS_API_URL }}
      - name: Deploy to Pages
        uses: cloudflare/wrangler-action@v3
        with:
          apiToken: \${{ secrets.CLOUDFLARE_API_TOKEN }}
          accountId: \${{ secrets.CLOUDFLARE_ACCOUNT_ID }}
          command: pages deploy apps/web/dist --project-name={{PAGES_PROJECT_NAME}}
\`\`\`

**Secrets 清單**：
| Secret | 說明 | 取得方式 |
|---|---|---|
| \`CLOUDFLARE_API_TOKEN\` | 需 Workers Scripts:Edit + Cloudflare Pages:Edit | Cloudflare Dashboard → My Profile → API Tokens |
| \`CLOUDFLARE_ACCOUNT_ID\` | Account ID | 右下角帳戶頁面 |
| \`CMS_API_URL\` | Worker 完整 URL | 首次部署 Worker 後取得 |

---

## 6. 本地部署腳本（PowerShell）

\`\`\`powershell
# scripts/deploy.ps1
[CmdletBinding()]
param(
    [switch]$Worker,
    [switch]$Web,
    [switch]$All
)

$OutputEncoding = [System.Text.Encoding]::UTF8
[Console]::OutputEncoding = [System.Text.Encoding]::UTF8

if ($All -or $Worker) {
    Write-Host "🚀 部署 Worker..." -ForegroundColor Cyan
    Set-Location apps/api
    npx wrangler deploy
    Set-Location ../..
}

if ($All -or $Web) {
    Write-Host "🌐 構建並部署前端..." -ForegroundColor Cyan
    Set-Location apps/web
    npm run build
    if ($LASTEXITCODE -eq 0) {
        npx wrangler pages deploy dist --project-name={{PAGES_PROJECT_NAME}}
    }
    Set-Location ../..
}

Write-Host "✅ 完成" -ForegroundColor Green
\`\`\`

---

## 7. 環境變數檔案模板

### apps/web/.env.example
\`\`\`
VITE_CMS_API_URL=https://{{PROJECT_NAME}}.{{YOUR_SUBDOMAIN}}.workers.dev
\`\`\`

### apps/api/.dev.vars（本地開發，不進 git）
\`\`\`
ADMIN_PASSWORD=local_dev_password_change_me
\`\`\`

---

## 8. 部署前檢查清單

- [ ] \`wrangler.toml\` 中的 \`compatibility_date\` 是否為近期日期
- [ ] KV namespace ID 是否正確（\`wrangler kv:namespace list\` 查詢）
- [ ] \`.gitignore\` 是否已排除 \`.env\`、\`.dev.vars\`、\`dist/\`、\`node_modules/\`
- [ ] GitHub Secrets 是否已設定（首次需手動）
- [ ] 生產密碼是否已用 \`wrangler secret put\` 設定
- [ ] \`VITE_CMS_API_URL\` 是否已填入實際 Worker URL（**絕不可 build 時為空**）
`;
